import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Chip, Pagination, Alert, Snackbar,
  CircularProgress, Grid, Tabs, Tab, Tooltip, Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import Layout from '../Layout';

// ========== Aba Histórico ==========

const AbaHistorico = ({ seguradoras, responsaveis, snack, setSnack }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pag, setPag] = useState({ page: 1, total: 0, total_paginas: 1 });
  const [filtros, setFiltros] = useState({ id_seguradora: '', id_responsavel: '', ativo: '' });
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({ id_seguradora: '', id_responsavel: '', data_inicio: '', data_fim: '' });
  const [formError, setFormError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchItems = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (filtros.id_seguradora) params.id_seguradora = filtros.id_seguradora;
      if (filtros.id_responsavel) params.id_responsavel = filtros.id_responsavel;
      if (filtros.ativo) params.ativo = filtros.ativo;
      const res = await api.get('/historico-responsaveis-seguradoras', { params });
      const d = res.data?.data || res.data || {};
      setItems(d.items || []);
      setPag({ page: d.page || 1, total: d.total || 0, total_paginas: d.total_paginas || 1 });
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { fetchItems(1); }, [fetchItems]);

  const abrirCriar = () => {
    setEditando(null);
    setFormData({ id_seguradora: '', id_responsavel: '', data_inicio: '', data_fim: '' });
    setFormError('');
    setDialogAberto(true);
  };

  const abrirEditar = (item) => {
    setEditando(item);
    setFormData({
      id_seguradora: item.id_seguradora,
      id_responsavel: item.id_responsavel,
      data_inicio: item.data_inicio || '',
      data_fim: item.data_fim || ''
    });
    setFormError('');
    setDialogAberto(true);
  };

  const salvar = async () => {
    setFormError('');
    if (!formData.id_seguradora || !formData.id_responsavel || !formData.data_inicio) {
      setFormError('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      const payload = {
        id_seguradora: formData.id_seguradora,
        id_responsavel: formData.id_responsavel,
        data_inicio: formData.data_inicio,
        data_fim: formData.data_fim || null
      };
      if (editando) {
        const res = await api.put(`/historico-responsaveis-seguradoras/${editando.id}`, payload);
        const bi = res.data?.data?.resultados_bi_atualizados;
        setSnack({ open: true, msg: `Registro atualizado com sucesso${bi ? `. ${bi} registro(s) do Resultados BI atualizados` : ''}`, sev: 'success' });
      } else {
        const res = await api.post('/historico-responsaveis-seguradoras', payload);
        const bi = res.data?.data?.resultados_bi_atualizados;
        setSnack({ open: true, msg: `Comercial cadastrado com sucesso${bi ? `. ${bi} registro(s) do Resultados BI atualizados` : ''}`, sev: 'success' });
      }
      setDialogAberto(false);
      fetchItems(pag.page);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Erro ao salvar');
    }
  };

  const excluir = async () => {
    if (!confirmDelete) return;
    try {
      const res = await api.delete(`/historico-responsaveis-seguradoras/${confirmDelete}`);
      const bi = res.data?.data?.resultados_bi_atualizados;
      setSnack({ open: true, msg: `Registro excluído${bi ? `. ${bi} registro(s) do Resultados BI atualizados` : ''}`, sev: 'success' });
      setConfirmDelete(null);
      fetchItems(pag.page);
    } catch (err) {
      setSnack({ open: true, msg: err.response?.data?.message || 'Erro ao excluir', sev: 'error' });
      setConfirmDelete(null);
    }
  };

  return (
    <>
      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Autocomplete
              size="small"
              options={seguradoras}
              getOptionLabel={(o) => o.nome || ''}
              value={seguradoras.find(s => s.id === filtros.id_seguradora) || null}
              onChange={(_, v) => setFiltros(f => ({ ...f, id_seguradora: v ? v.id : '' }))}
              renderInput={(params) => <TextField {...params} label="Seguradora" />}
              ListboxProps={{ style: { maxHeight: 200 } }}
              noOptionsText="Nenhuma encontrada"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              size="small"
              options={responsaveis}
              getOptionLabel={(o) => o.Responsavel || o.nome || ''}
              value={responsaveis.find(r => r.id === filtros.id_responsavel) || null}
              onChange={(_, v) => setFiltros(f => ({ ...f, id_responsavel: v ? v.id : '' }))}
              renderInput={(params) => <TextField {...params} label="Comercial" />}
              ListboxProps={{ style: { maxHeight: 200 } }}
              noOptionsText="Nenhum encontrado"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={filtros.ativo} onChange={e => setFiltros(f => ({ ...f, ativo: e.target.value }))}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Ativo</MenuItem>
                <MenuItem value="false">Encerrado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={() => fetchItems(1)}>Filtrar</Button>
            <Button variant="outlined" size="small" onClick={() => { setFiltros({ id_seguradora: '', id_responsavel: '', ativo: '' }); }}>Limpar</Button>
            <Button variant="contained" size="small" color="success" startIcon={<AddIcon />} onClick={abrirCriar}>Novo</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <Paper>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
        {!loading && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Seguradora</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Comercial</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data Início</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data Fim</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhum registro encontrado</TableCell></TableRow>
                ) : items.map(item => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.seguradora_nome}</TableCell>
                    <TableCell>{item.responsavel_nome}</TableCell>
                    <TableCell>{item.data_inicio ? new Date(item.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</TableCell>
                    <TableCell>{item.data_fim ? new Date(item.data_fim + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</TableCell>
                    <TableCell align="center">
                      {item.ativo
                        ? <Chip label="Ativo" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                        : <Chip label="Encerrado" size="small" sx={{ bgcolor: '#f5f5f5', color: '#757575', fontWeight: 600 }} />
                      }
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(item)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => setConfirmDelete(item.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pag.total_paginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Typography variant="caption" color="text.secondary">{pag.total} registros</Typography>
            <Pagination count={pag.total_paginas} page={pag.page} onChange={(_, p) => fetchItems(p)} color="primary" size="small" />
          </Box>
        )}
        {pag.total_paginas <= 1 && pag.total > 0 && (
          <Box sx={{ p: 1, textAlign: 'right' }}><Typography variant="caption" color="text.secondary">{pag.total} registros</Typography></Box>
        )}
      </Paper>

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar Comercial' : 'Novo Comercial por Seguradora'}</DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={seguradoras}
                getOptionLabel={(o) => o.nome || ''}
                value={seguradoras.find(s => s.id === formData.id_seguradora) || null}
                onChange={(_, v) => setFormData(f => ({ ...f, id_seguradora: v ? v.id : '' }))}
                disabled={!!editando}
                renderInput={(params) => <TextField {...params} label="Seguradora" required />}
                ListboxProps={{ style: { maxHeight: 200 } }}
                noOptionsText="Nenhuma encontrada"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={responsaveis}
                getOptionLabel={(o) => o.Responsavel || o.nome || ''}
                value={responsaveis.find(r => r.id === formData.id_responsavel) || null}
                onChange={(_, v) => setFormData(f => ({ ...f, id_responsavel: v ? v.id : '' }))}
                renderInput={(params) => <TextField {...params} label="Comercial" required />}
                ListboxProps={{ style: { maxHeight: 200 } }}
                noOptionsText="Nenhum encontrado"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" type="date" label="Data Início" required
                value={formData.data_inicio}
                onChange={e => setFormData(f => ({ ...f, data_inicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" type="date" label="Data Fim"
                value={formData.data_fim}
                onChange={e => setFormData(f => ({ ...f, data_fim: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Deixe em branco para comercial ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)} color="inherit">Cancelar</Button>
          <Button onClick={salvar} variant="contained">{editando ? 'Atualizar' : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação de exclusão */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir Registro</DialogTitle>
        <DialogContent><Typography>Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="inherit">Cancelar</Button>
          <Button onClick={excluir} color="error" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


// ========== Aba Substituições ==========

const AbaSubstituicoes = ({ seguradoras, responsaveis, snack, setSnack }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pag, setPag] = useState({ page: 1, total: 0, total_paginas: 1 });
  const [filtros, setFiltros] = useState({ id_seguradora: '', id_responsavel_substituto: '' });
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({ id_seguradora: '', id_responsavel_substituto: '', data_inicio: '', data_fim: '' });
  const [formError, setFormError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [comercialAtivo, setComercialAtivo] = useState(null);

  const fetchItems = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (filtros.id_seguradora) params.id_seguradora = filtros.id_seguradora;
      if (filtros.id_responsavel_substituto) params.id_responsavel_substituto = filtros.id_responsavel_substituto;
      const res = await api.get('/substituicoes-responsaveis-seguradoras', { params });
      const d = res.data?.data || res.data || {};
      setItems(d.items || []);
      setPag({ page: d.page || 1, total: d.total || 0, total_paginas: d.total_paginas || 1 });
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { fetchItems(1); }, [fetchItems]);

  const fetchComercialAtivo = async (idSeg) => {
    if (!idSeg) { setComercialAtivo(null); return; }
    try {
      const res = await api.get(`/historico-responsaveis-seguradoras/ativo/${idSeg}`);
      setComercialAtivo(res.data?.data || null);
    } catch {
      setComercialAtivo(null);
    }
  };

  const getStatusSub = (item) => {
    const agora = new Date();
    const inicio = new Date(item.data_inicio);
    const fim = new Date(item.data_fim);
    if (agora < inicio) return { label: 'Programado', sx: { bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 } };
    if (agora > fim) return { label: 'Encerrado', sx: { bgcolor: '#f5f5f5', color: '#757575', fontWeight: 600 } };
    return { label: 'Em vigor', sx: { bgcolor: '#fff8e1', color: '#f57f17', fontWeight: 600 } };
  };

  const abrirCriar = () => {
    setEditando(null);
    setFormData({ id_seguradora: '', id_responsavel_substituto: '', data_inicio: '', data_fim: '' });
    setComercialAtivo(null);
    setFormError('');
    setDialogAberto(true);
  };

  const abrirEditar = (item) => {
    setEditando(item);
    setFormData({
      id_seguradora: item.id_seguradora,
      id_responsavel_substituto: item.id_responsavel_substituto,
      data_inicio: item.data_inicio ? item.data_inicio.slice(0, 16) : '',
      data_fim: item.data_fim ? item.data_fim.slice(0, 16) : ''
    });
    fetchComercialAtivo(item.id_seguradora);
    setFormError('');
    setDialogAberto(true);
  };

  const handleSegChange = (idSeg) => {
    setFormData(f => ({ ...f, id_seguradora: idSeg }));
    fetchComercialAtivo(idSeg);
  };

  const salvar = async () => {
    setFormError('');
    if (!formData.id_seguradora || !formData.id_responsavel_substituto || !formData.data_inicio || !formData.data_fim) {
      setFormError('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      const payload = {
        id_seguradora: formData.id_seguradora,
        id_responsavel_substituto: formData.id_responsavel_substituto,
        data_inicio: formData.data_inicio,
        data_fim: formData.data_fim
      };
      if (editando) {
        const res = await api.put(`/substituicoes-responsaveis-seguradoras/${editando.id}`, payload);
        const bi = res.data?.data?.resultados_bi_atualizados;
        setSnack({ open: true, msg: `Substituição atualizada com sucesso${bi ? `. ${bi} registro(s) do Resultados BI atualizados` : ''}`, sev: 'success' });
      } else {
        const res = await api.post('/substituicoes-responsaveis-seguradoras', payload);
        const bi = res.data?.data?.resultados_bi_atualizados;
        setSnack({ open: true, msg: `Substituição cadastrada com sucesso${bi ? `. ${bi} registro(s) do Resultados BI atualizados` : ''}`, sev: 'success' });
      }
      setDialogAberto(false);
      fetchItems(pag.page);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Erro ao salvar');
    }
  };

  const excluir = async () => {
    if (!confirmDelete) return;
    try {
      const res = await api.delete(`/substituicoes-responsaveis-seguradoras/${confirmDelete}`);
      const bi = res.data?.data?.resultados_bi_atualizados;
      setSnack({ open: true, msg: `Substituição excluída${bi ? `. ${bi} registro(s) do Resultados BI atualizados` : ''}`, sev: 'success' });
      setConfirmDelete(null);
      fetchItems(pag.page);
    } catch (err) {
      setSnack({ open: true, msg: err.response?.data?.message || 'Erro ao excluir', sev: 'error' });
      setConfirmDelete(null);
    }
  };

  // Buscar comercial principal para exibição na tabela
  const [principaisMap, setPrincipaisMap] = useState({});

  useEffect(() => {
    const segIds = [...new Set(items.map(i => i.id_seguradora))];
    const novos = {};
    const promises = segIds.filter(id => !(id in principaisMap)).map(async id => {
      try {
        const res = await api.get(`/historico-responsaveis-seguradoras/ativo/${id}`);
        novos[id] = res.data?.data?.responsavel_nome || '—';
      } catch {
        novos[id] = '—';
      }
    });
    if (promises.length > 0) {
      Promise.all(promises).then(() => setPrincipaisMap(prev => ({ ...prev, ...novos })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <>
      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Autocomplete
              size="small"
              options={seguradoras}
              getOptionLabel={(o) => o.nome || ''}
              value={seguradoras.find(s => s.id === filtros.id_seguradora) || null}
              onChange={(_, v) => setFiltros(f => ({ ...f, id_seguradora: v ? v.id : '' }))}
              renderInput={(params) => <TextField {...params} label="Seguradora" />}
              ListboxProps={{ style: { maxHeight: 200 } }}
              noOptionsText="Nenhuma encontrada"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              size="small"
              options={responsaveis}
              getOptionLabel={(o) => o.Responsavel || o.nome || ''}
              value={responsaveis.find(r => r.id === filtros.id_responsavel_substituto) || null}
              onChange={(_, v) => setFiltros(f => ({ ...f, id_responsavel_substituto: v ? v.id : '' }))}
              renderInput={(params) => <TextField {...params} label="Substituto" />}
              ListboxProps={{ style: { maxHeight: 200 } }}
              noOptionsText="Nenhum encontrado"
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={() => fetchItems(1)}>Filtrar</Button>
            <Button variant="outlined" size="small" onClick={() => { setFiltros({ id_seguradora: '', id_responsavel_substituto: '' }); }}>Limpar</Button>
            <Button variant="contained" size="small" color="success" startIcon={<AddIcon />} onClick={abrirCriar}>Nova</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <Paper>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
        {!loading && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Seguradora</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Comercial Principal</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Substituto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data Início</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data Fim</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nenhuma substituição encontrada</TableCell></TableRow>
                ) : items.map(item => {
                  const st = getStatusSub(item);
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.seguradora_nome}</TableCell>
                      <TableCell>{principaisMap[item.id_seguradora] || '—'}</TableCell>
                      <TableCell>{item.responsavel_substituto_nome}</TableCell>
                      <TableCell>{item.data_inicio ? new Date(item.data_inicio).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</TableCell>
                      <TableCell>{item.data_fim ? new Date(item.data_fim).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</TableCell>
                      <TableCell align="center"><Chip label={st.label} size="small" sx={st.sx} /></TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(item)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => setConfirmDelete(item.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pag.total_paginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Typography variant="caption" color="text.secondary">{pag.total} registros</Typography>
            <Pagination count={pag.total_paginas} page={pag.page} onChange={(_, p) => fetchItems(p)} color="primary" size="small" />
          </Box>
        )}
        {pag.total_paginas <= 1 && pag.total > 0 && (
          <Box sx={{ p: 1, textAlign: 'right' }}><Typography variant="caption" color="text.secondary">{pag.total} registros</Typography></Box>
        )}
      </Paper>

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar Substituição' : 'Nova Substituição Temporária'}</DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={seguradoras}
                getOptionLabel={(o) => o.nome || ''}
                value={seguradoras.find(s => s.id === formData.id_seguradora) || null}
                onChange={(_, v) => handleSegChange(v ? v.id : '')}
                disabled={!!editando}
                renderInput={(params) => <TextField {...params} label="Seguradora" required />}
                ListboxProps={{ style: { maxHeight: 200 } }}
                noOptionsText="Nenhuma encontrada"
              />
            </Grid>
            {comercialAtivo && (
              <Grid item xs={12}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">Comercial principal ativo</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>{comercialAtivo.responsavel_nome}</Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={responsaveis}
                getOptionLabel={(o) => o.Responsavel || o.nome || ''}
                value={responsaveis.find(r => r.id === formData.id_responsavel_substituto) || null}
                onChange={(_, v) => setFormData(f => ({ ...f, id_responsavel_substituto: v ? v.id : '' }))}
                renderInput={(params) => <TextField {...params} label="Substituto" required />}
                ListboxProps={{ style: { maxHeight: 200 } }}
                noOptionsText="Nenhum encontrado"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" type="datetime-local" label="Data Início" required
                value={formData.data_inicio}
                onChange={e => setFormData(f => ({ ...f, data_inicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" type="datetime-local" label="Data Fim" required
                value={formData.data_fim}
                onChange={e => setFormData(f => ({ ...f, data_fim: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)} color="inherit">Cancelar</Button>
          <Button onClick={salvar} variant="contained">{editando ? 'Atualizar' : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação de exclusão */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir Substituição</DialogTitle>
        <DialogContent><Typography>Tem certeza que deseja excluir esta substituição? Esta ação não pode ser desfeita.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="inherit">Cancelar</Button>
          <Button onClick={excluir} color="error" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


// ========== Componente Principal ==========

const ComerciaisSeguradorasPanel = () => {
  const [tab, setTab] = useState(0);
  const [seguradoras, setSeguradoras] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { window.location.href = '/login'; return; }

    api.get('/seguradoras-por-seguradora').then(res => {
      setSeguradoras(res.data?.data || res.data || []);
    }).catch(() => {});

    api.get('/responsaveis').then(res => {
      const raw = res.data;
      setResponsaveis(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
    }).catch(() => {});
  }, []);

  return (
    <Layout>
      <Box sx={{ py: 3, px: 2 }}>
        <Typography variant="h5" gutterBottom>Comerciais por Seguradora</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Gerencie o vínculo de comerciais para seguradoras que usam o modelo "comercial por seguradora"
        </Typography>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>

        <Paper sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
            <Tab label="Histórico de Comerciais" />
            <Tab label="Substituições Temporárias" />
          </Tabs>
        </Paper>

        {tab === 0 && <AbaHistorico seguradoras={seguradoras} responsaveis={responsaveis} snack={snack} setSnack={setSnack} />}
        {tab === 1 && <AbaSubstituicoes seguradoras={seguradoras} responsaveis={responsaveis} snack={snack} setSnack={setSnack} />}
      </Box>
    </Layout>
  );
};

export default ComerciaisSeguradorasPanel;
